import DOMPurify from "dompurify";

/**
 * The tags and attributes authored rich text may use. Anything else is dropped,
 * so content authored outside this vocabulary loses meaning silently — the
 * authoring surfaces on the other side of the API enforce the same list.
 */
const ALLOWED_TAGS = [
  "p",
  "br",
  "ul",
  "ol",
  "li",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "span",
  "a",
] as const;

const ALLOWED_ATTRIBUTES = ["href", "target", "rel"] as const;

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

function isSafeHref(href: string): boolean {
  return SAFE_HREF.test(href.trim());
}

/**
 * Sanitized rich text, ready for `dangerouslySetInnerHTML`.
 *
 * Anchors are post-processed after DOMPurify rather than trusted from it: one
 * that survives with an unsafe scheme is unwrapped to its text, and one that
 * survives safely is forced to open in a new tab with `rel="noopener"`. Returns
 * "" without a DOM, since there is nothing to sanitize against.
 */
export function sanitizeRichTextHtml(html: string): string {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return "";
  }

  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [...ALLOWED_ATTRIBUTES],
    ALLOWED_URI_REGEXP: SAFE_HREF,
    ALLOW_DATA_ATTR: false,
  });

  const template = document.createElement("template");
  template.innerHTML = sanitized;

  template.content.querySelectorAll("a").forEach((anchor) => {
    const href = anchor.getAttribute("href");

    if (!href || !isSafeHref(href)) {
      anchor.replaceWith(...Array.from(anchor.childNodes));
      return;
    }

    anchor.setAttribute("target", "_blank");
    anchor.setAttribute("rel", "noopener noreferrer");
  });

  return template.innerHTML.trim();
}
